<template>
  <div class="store-home">
    <div class="page-header">
      <h2>商城概览</h2>
      <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadAllData">刷新数据</el-button>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :span="6">
        <el-card class="stats-card">
          <div class="stats-content">
            <div class="stats-icon goods">
              <i class="el-icon-goods"></i>
            </div>
            <div class="stats-info">
              <div class="stats-number">{{ stats.total_goods || 0 }}</div>
              <div class="stats-label">商品总数</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card">
          <div class="stats-content">
            <div class="stats-icon orders">
              <i class="el-icon-document-checked"></i>
            </div>
            <div class="stats-info">
              <div class="stats-number">{{ stats.total_orders || 0 }}</div>
              <div class="stats-label">订单总数</div>
              <div class="stats-extra">今日: {{ stats.today_orders || 0 }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card">
          <div class="stats-content">
            <div class="stats-icon sales">
              <i class="el-icon-money"></i>
            </div>
            <div class="stats-info">
              <div class="stats-number">¥{{ formatMoney(stats.total_sales) }}</div>
              <div class="stats-label">销售总额</div>
              <div class="stats-extra">今日: ¥{{ formatMoney(stats.today_sales) }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card">
          <div class="stats-content">
            <div class="stats-icon shops">
              <i class="el-icon-office-building"></i>
            </div>
            <div class="stats-info">
              <div class="stats-number">{{ stats.total_shops || 0 }}</div>
              <div class="stats-label">店铺总数</div>
              <div class="stats-extra warning" v-if="stats.low_stock_goods > 0">
                库存预警: {{ stats.low_stock_goods }}
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <el-row :gutter="20" class="charts-section">
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>近7天销售趋势</span>
            <el-button type="text" size="small" @click="loadSalesTrend">刷新</el-button>
          </div>
          <div class="chart-container" v-loading="loadingTrend">
            <div class="simple-chart">
              <div class="chart-legend">
                <span class="legend-item">
                  <i class="legend-dot" style="background: #409EFF;"></i>
                  订单数
                </span>
                <span class="legend-item">
                  <i class="legend-dot" style="background: #67C23A;"></i>
                  销售额
                </span>
              </div>
              <div class="chart-bars">
                <div v-for="(date, index) in salesTrend.dates" :key="index" class="bar-group">
                  <div class="bar-container">
                    <div 
                      class="bar bar-orders" 
                      :style="{ height: getBarHeight(salesTrend.order_counts[index], maxOrderCount) }"
                      :title="`订单: ${salesTrend.order_counts[index]}`"
                    ></div>
                    <div 
                      class="bar bar-sales" 
                      :style="{ height: getBarHeight(salesTrend.sales_amounts[index], maxSalesAmount) }"
                      :title="`销售额: ¥${salesTrend.sales_amounts[index]}`"
                    ></div>
                  </div>
                  <div class="bar-label">{{ date }}</div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="chart-card">
          <div slot="header" class="chart-header">
            <span>商品分类分布</span>
            <el-button type="text" size="small" @click="loadCategoryDistribution">刷新</el-button>
          </div>
          <div class="chart-container" v-loading="loadingCategory">
            <div class="category-chart">
              <div v-if="categoryDistribution.values.length === 0" class="empty-chart">
                <i class="el-icon-pie-chart"></i>
                <p>暂无分类数据</p>
              </div>
              <div v-else class="category-list">
                <div 
                  v-for="(item, index) in categoryDistribution.values" 
                  :key="index" 
                  class="category-item"
                >
                  <div class="category-info">
                    <span class="category-name">{{ item.name }}</span>
                    <span class="category-value">{{ item.value }} 件</span>
                  </div>
                  <div class="category-bar">
                    <div 
                      class="category-bar-fill" 
                      :style="{ 
                        width: getCategoryPercent(item.value) + '%',
                        background: getCategoryColor(index)
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 最新订单和热销商品 -->
    <el-row :gutter="20" class="data-section">
      <el-col :span="12">
        <el-card class="data-card">
          <div slot="header" class="data-header">
            <span>最新订单</span>
            <el-button type="text" @click="$router.push('/dashboard/store/order')">查看全部</el-button>
          </div>
          <div class="data-list" v-loading="loadingOrders">
            <div v-if="latestOrders.length === 0" class="empty-data">
              <i class="el-icon-document-remove"></i>
              <p>暂无订单数据</p>
            </div>
            <div v-else>
              <div v-for="order in latestOrders" :key="order.id" class="order-item">
                <div class="order-info">
                  <div class="order-no">{{ order.order_no }}</div>
                  <div class="order-detail">
                    <span class="order-user">{{ order.user_name || order.user_mobile }}</span>
                    <span class="order-time">{{ formatTime(order.create_time) }}</span>
                  </div>
                </div>
                <div class="order-right">
                  <div class="order-amount">¥{{ order.order_amount }}</div>
                  <el-tag :type="getStatusType(order.order_status)" size="mini">
                    {{ order.status_text }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="data-card">
          <div slot="header" class="data-header">
            <span>热销商品</span>
            <el-button type="text" @click="$router.push('/dashboard/store/goods')">查看全部</el-button>
          </div>
          <div class="data-list" v-loading="loadingGoods">
            <div v-if="hotGoods.length === 0" class="empty-data">
              <i class="el-icon-goods"></i>
              <p>暂无商品数据</p>
            </div>
            <div v-else>
              <div v-for="goods in hotGoods" :key="goods.id" class="goods-item">
                <el-image 
                  v-if="goods.image" 
                  :src="goods.image" 
                  class="goods-image" 
                  fit="cover"
                  :preview-src-list="[goods.image]"
                />
                <div v-else class="goods-image-placeholder">
                  <i class="el-icon-picture-outline"></i>
                </div>
                <div class="goods-info">
                  <div class="goods-name" :title="goods.goods_name">{{ goods.goods_name }}</div>
                  <div class="goods-detail">
                    <span class="goods-price">¥{{ goods.price }}</span>
                    <span class="goods-sales">销量: {{ goods.sales_count }}</span>
                    <span class="goods-stock" :class="{ 'low-stock': goods.stock < 10 }">
                      库存: {{ goods.stock }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 快捷操作 -->
    <el-row class="quick-actions">
      <el-card>
        <div slot="header" class="actions-header">
          <span>快捷操作</span>
        </div>
        <div class="actions-grid">
          <div class="action-item" @click="$router.push('/dashboard/store/goods')">
            <i class="el-icon-plus"></i>
            <span>添加商品</span>
          </div>
          <div class="action-item" @click="$router.push('/dashboard/store/order')">
            <i class="el-icon-s-check"></i>
            <span>处理订单</span>
          </div>
          <div class="action-item" @click="$router.push('/dashboard/store/category')">
            <i class="el-icon-menu"></i>
            <span>管理分类</span>
          </div>
          <div class="action-item" @click="$router.push('/dashboard/store/shop')">
            <i class="el-icon-office-building"></i>
            <span>店铺设置</span>
          </div>
        </div>
      </el-card>
    </el-row>
  </div>
</template>

<script>
import { 
  getOverviewStats, 
  getSalesTrend, 
  getCategoryDistribution,
  getLatestOrders,
  getHotGoods
} from '@/api/store'

export default {
  name: 'StoreHome',
  data() {
    return {
      stats: {
        total_goods: 0,
        total_orders: 0,
        total_sales: 0,
        total_shops: 0,
        today_orders: 0,
        today_sales: 0,
        pending_orders: 0,
        low_stock_goods: 0
      },
      salesTrend: {
        dates: [],
        order_counts: [],
        sales_amounts: []
      },
      categoryDistribution: {
        names: [],
        values: []
      },
      latestOrders: [],
      hotGoods: [],
      loadingTrend: false,
      loadingCategory: false,
      loadingOrders: false,
      loadingGoods: false
    }
  },
  computed: {
    maxOrderCount() {
      return Math.max(...this.salesTrend.order_counts, 1)
    },
    maxSalesAmount() {
      return Math.max(...this.salesTrend.sales_amounts, 1)
    },
    maxCategoryValue() {
      if (this.categoryDistribution.values.length === 0) return 1
      return Math.max(...this.categoryDistribution.values.map(v => v.value), 1)
    }
  },
  mounted() {
    this.loadAllData()
  },
  methods: {
    async loadAllData() {
      await Promise.all([
        this.loadOverviewStats(),
        this.loadSalesTrend(),
        this.loadCategoryDistribution(),
        this.loadLatestOrders(),
        this.loadHotGoods()
      ])
    },
    
    async loadOverviewStats() {
      try {
        const res = await getOverviewStats()
        this.stats = res.data || this.stats
      } catch (error) {
        console.error('加载概览统计失败:', error)
        this.$message.error('加载概览统计失败')
      }
    },
    
    async loadSalesTrend() {
      this.loadingTrend = true
      try {
        const res = await getSalesTrend({ days: 7 })
        this.salesTrend = res.data || this.salesTrend
      } catch (error) {
        console.error('加载销售趋势失败:', error)
        this.$message.error('加载销售趋势失败')
      } finally {
        this.loadingTrend = false
      }
    },
    
    async loadCategoryDistribution() {
      this.loadingCategory = true
      try {
        const res = await getCategoryDistribution()
        this.categoryDistribution = res.data || this.categoryDistribution
      } catch (error) {
        console.error('加载分类分布失败:', error)
        this.$message.error('加载分类分布失败')
      } finally {
        this.loadingCategory = false
      }
    },
    
    async loadLatestOrders() {
      this.loadingOrders = true
      try {
        const res = await getLatestOrders({ limit: 10 })
        this.latestOrders = res.data || []
      } catch (error) {
        console.error('加载最新订单失败:', error)
        this.$message.error('加载最新订单失败')
      } finally {
        this.loadingOrders = false
      }
    },
    
    async loadHotGoods() {
      this.loadingGoods = true
      try {
        const res = await getHotGoods({ limit: 10 })
        this.hotGoods = res.data || []
      } catch (error) {
        console.error('加载热销商品失败:', error)
        this.$message.error('加载热销商品失败')
      } finally {
        this.loadingGoods = false
      }
    },
    
    formatMoney(value) {
      if (!value) return '0.00'
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
      
      return time.substring(5, 16)
    },
    
    getBarHeight(value, max) {
      if (!max || !value) return '0%'
      return Math.max((value / max * 100), 2) + '%'
    },
    
    getCategoryPercent(value) {
      if (!this.maxCategoryValue || !value) return 0
      return (value / this.maxCategoryValue * 100).toFixed(1)
    },
    
    getCategoryColor(index) {
      const colors = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
        'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)'
      ]
      return colors[index % colors.length]
    },
    
    getStatusType(status) {
      const statusMap = {
        0: 'warning',  // 待付款
        1: 'primary',  // 待发货
        2: 'info',     // 待收货
        3: 'success',  // 已完成
        4: 'danger',   // 已取消
        5: 'warning'   // 售后中
      }
      return statusMap[status] || 'info'
    }
  }
}
</script>

<style scoped>
.store-home {
  padding: 0;
}

.page-header {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

/* 统计卡片样式 */
.stats-cards {
  margin-bottom: 20px;
}

.stats-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
}

.stats-content {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.stats-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 24px;
  color: #fff;
}

.stats-icon.goods {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stats-icon.orders {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stats-icon.sales {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stats-icon.shops {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stats-info {
  flex: 1;
}

.stats-number {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  line-height: 1;
  margin-bottom: 8px;
}

.stats-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.stats-extra {
  font-size: 12px;
  color: #999;
}

.stats-extra.warning {
  color: #E6A23C;
  font-weight: 500;
}

/* 图表区域样式 */
.charts-section {
  margin-bottom: 20px;
}

.chart-card {
  height: 350px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.chart-container {
  height: 280px;
  padding: 10px 0;
}

/* 简单柱状图 */
.simple-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #666;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 6px;
}

.chart-bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  padding: 0 10px;
}

.bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 80px;
}

.bar-container {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.bar {
  flex: 1;
  min-height: 2px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.bar:hover {
  opacity: 0.8;
  transform: scaleY(1.05);
}

.bar-orders {
  background: linear-gradient(180deg, #409EFF 0%, #66B1FF 100%);
}

.bar-sales {
  background: linear-gradient(180deg, #67C23A 0%, #85CE61 100%);
}

.bar-label {
  font-size: 11px;
  color: #999;
  text-align: center;
}

/* 分类图表 */
.category-chart {
  height: 100%;
  overflow-y: auto;
}

.empty-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.empty-chart i {
  font-size: 48px;
  margin-bottom: 10px;
}

.category-list {
  padding: 10px 0;
}

.category-item {
  margin-bottom: 16px;
}

.category-item:last-child {
  margin-bottom: 0;
}

.category-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
}

.category-name {
  color: #333;
  font-weight: 500;
}

.category-value {
  color: #666;
}

.category-bar {
  height: 20px;
  background: #f5f5f5;
  border-radius: 10px;
  overflow: hidden;
}

.category-bar-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.5s ease;
}

/* 数据列表样式 */
.data-section {
  margin-bottom: 20px;
}

.data-card {
  height: 450px;
}

.data-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.data-list {
  height: 380px;
  overflow-y: auto;
}

.data-list::-webkit-scrollbar {
  width: 6px;
}

.data-list::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.empty-data {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
}

.empty-data i {
  font-size: 48px;
  margin-bottom: 10px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.order-item:hover {
  background: #f9f9f9;
  padding-left: 8px;
  padding-right: 8px;
  margin-left: -8px;
  margin-right: -8px;
  border-radius: 4px;
}

.order-item:last-child {
  border-bottom: none;
}

.order-info {
  flex: 1;
  min-width: 0;
}

.order-no {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.order-detail {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.order-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-left: 12px;
}

.order-amount {
  color: #f56c6c;
  font-weight: 600;
  font-size: 14px;
}

.order-time {
  color: #999;
}

.goods-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.goods-item:hover {
  background: #f9f9f9;
  padding-left: 8px;
  padding-right: 8px;
  margin-left: -8px;
  margin-right: -8px;
  border-radius: 4px;
}

.goods-item:last-child {
  border-bottom: none;
}

.goods-image {
  width: 50px;
  height: 50px;
  border-radius: 6px;
  margin-right: 12px;
  flex-shrink: 0;
  cursor: pointer;
}

.goods-image-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 6px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  margin-right: 12px;
  flex-shrink: 0;
}

.goods-info {
  flex: 1;
  min-width: 0;
}

.goods-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.goods-detail {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.goods-price {
  color: #f56c6c;
  font-weight: 600;
}

.goods-stock.low-stock {
  color: #E6A23C;
  font-weight: 500;
}

/* 快捷操作样式 */
.quick-actions {
  margin-bottom: 20px;
}

.actions-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 20px;
  padding: 20px 0;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.action-item i {
  font-size: 28px;
  color: #409eff;
  margin-bottom: 10px;
}

.action-item span {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}
</style>
